import { useState, useEffect } from 'react';
import axios from 'axios';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams, Link } from 'react-router-dom';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

// Flattens the nested category tree, indenting by depth — same helper
// used in PromoCardForm, kept local here to avoid a shared-utils change.
const flattenCategories = (nodes, depth = 0) => {
    let list = [];
    nodes.forEach((node) => {
        list.push({ _id: node._id, label: `${'—'.repeat(depth)} ${node.name}`.trim() });
        if (node.children?.length) {
            list = list.concat(flattenCategories(node.children, depth + 1));
        }
    });
    return list;
};

const NotificationForm = () => {
    const { id } = useParams();
    const isEditMode = Boolean(id);

    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(isEditMode);
    const [saving, setSaving] = useState(false);

    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [type, setType] = useState("product");

    // Offer type
    const [offer, setOffer] = useState(null); // { _id, name } | null
    const [offerOptions, setOfferOptions] = useState([]);

    // Product type
    const [productTargetMode, setProductTargetMode] = useState("single");
    const [product, setProduct] = useState(null); // { _id, name } | null
    const [category, setCategory] = useState(""); // category _id
    const [products, setProducts] = useState([]); // [{ _id, name }]
    const [productOptions, setProductOptions] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);

    // Alert type
    const [targetUsers, setTargetUsers] = useState([]); // [{ _id, name, email }]
    const [userOptions, setUserOptions] = useState([]);

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const [offersRes, productsRes, categoriesRes, usersRes] = await Promise.all([
                    axios.get('/api/v1/admin/offers'),
                    axios.get('/api/v1/admin/products'),
                    axios.get('/api/v1/categories'),
                    axios.get('/api/v1/admin/users'),
                ]);
                setOfferOptions(offersRes.data.offers.map((o) => ({ _id: o._id, name: o.name })));
                setProductOptions(productsRes.data.products.map((p) => ({ _id: p._id, name: p.name })));
                setCategoryOptions(flattenCategories(categoriesRes.data.categories));
                setUserOptions(usersRes.data.users.map((u) => ({ _id: u._id, name: u.name, email: u.email })));
            } catch (error) {
                console.error('Failed to load picker options', error);
            }
        };
        fetchOptions();
    }, []);

    useEffect(() => {
        if (!isEditMode) return;

        const fetchNotification = async () => {
            try {
                const { data } = await axios.get('/api/v1/admin/notifications');
                const n = data.notifications.find((item) => item._id === id);
                if (!n) {
                    enqueueSnackbar("Notification not found", { variant: "error" });
                    navigate('/admin/notifications');
                    return;
                }
                setTitle(n.title);
                setBody(n.body);
                setType(n.type);
                setOffer(n.offer ? { _id: n.offer._id, name: n.offer.name } : null);
                setProductTargetMode(n.productTargetMode || "single");
                setProduct(n.product ? { _id: n.product._id, name: n.product.name } : null);
                setCategory(n.category?._id || "");
                setProducts(n.products?.map((p) => ({ _id: p._id, name: p.name })) || []);
                setTargetUsers(n.targetUsers?.map((u) => ({ _id: u._id, name: u.name, email: u.email })) || []);
            } catch (error) {
                enqueueSnackbar(error?.response?.data?.message || "Failed to load notification", { variant: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchNotification();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const submitHandler = async (e) => {
        e.preventDefault();

        if (!title.trim()) {
            enqueueSnackbar("Please enter a title", { variant: "warning" });
            return;
        }
        if (!body.trim()) {
            enqueueSnackbar("Please enter the notification text", { variant: "warning" });
            return;
        }
        if (type === 'offer' && !offer) {
            enqueueSnackbar("Please choose an offer", { variant: "warning" });
            return;
        }
        if (type === 'product') {
            if (productTargetMode === 'single' && !product) {
                enqueueSnackbar("Please choose a product", { variant: "warning" });
                return;
            }
            if (productTargetMode === 'category' && !category) {
                enqueueSnackbar("Please choose a category", { variant: "warning" });
                return;
            }
            if (productTargetMode === 'multiple' && products.length === 0) {
                enqueueSnackbar("Please choose at least one product", { variant: "warning" });
                return;
            }
        }

        const payload = {
            title,
            body,
            type,
            offer: offer?._id,
            productTargetMode,
            product: product?._id,
            category,
            products: products.map((p) => p._id),
            targetUsers: targetUsers.map((u) => u._id),
        };

        setSaving(true);
        try {
            if (isEditMode) {
                await axios.put(`/api/v1/admin/notification/${id}`, payload);
                enqueueSnackbar("Notification updated", { variant: "success" });
            } else {
                await axios.post('/api/v1/admin/notification/new', payload);
                enqueueSnackbar("Notification created", { variant: "success" });
            }
            navigate('/admin/notifications');
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to save notification", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <BackdropLoader />;

    return (
        <>
            <MetaData title={`Admin: ${isEditMode ? "Edit" : "New"} Notification | 1313 Mart`} />
            {saving && <BackdropLoader />}

            <form onSubmit={submitHandler} className="flex flex-col gap-4 bg-white rounded-lg shadow p-4 sm:p-6" id="notificationform">

                <h1 className="text-lg font-medium uppercase">{isEditMode ? "Edit" : "New"} Notification</h1>

                <TextField
                    label="Title"
                    variant="outlined"
                    size="small"
                    required
                    fullWidth
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                />

                {/* --- Type selector --- */}
                <div className="border rounded-lg p-4 flex flex-col gap-4 bg-gray-50">
                    <p className="text-sm font-medium text-gray-700">Notification Type</p>
                    <RadioGroup row value={type} onChange={(e) => setType(e.target.value)}>
                        <FormControlLabel value="offer" control={<Radio />} label="Offer" />
                        <FormControlLabel value="product" control={<Radio />} label="Product" />
                        <FormControlLabel value="alert" control={<Radio />} label="Alert" />
                    </RadioGroup>

                    {/* Offer type */}
                    {type === 'offer' && (
                        <div className="flex flex-col gap-2">
                            <Autocomplete
                                options={offerOptions}
                                getOptionLabel={(opt) => opt.name || ''}
                                isOptionEqualToValue={(opt, val) => opt._id === val._id}
                                value={offer}
                                onChange={(e, newValue) => setOffer(newValue)}
                                renderInput={(params) => (
                                    <TextField {...params} label="Choose an offer" size="small" />
                                )}
                            />
                            <Link to="/admin/offer/new" className="text-sm text-primary-blue hover:underline w-fit">
                                + Create a new offer
                            </Link>
                        </div>
                    )}

                    {/* Product type */}
                    {type === 'product' && (
                        <div className="flex flex-col gap-3">
                            <RadioGroup row value={productTargetMode} onChange={(e) => setProductTargetMode(e.target.value)}>
                                <FormControlLabel value="single" control={<Radio size="small" />} label="One product" />
                                <FormControlLabel value="category" control={<Radio size="small" />} label="A category" />
                                <FormControlLabel value="multiple" control={<Radio size="small" />} label="Multiple products" />
                            </RadioGroup>

                            {productTargetMode === 'single' && (
                                <Autocomplete
                                    options={productOptions}
                                    getOptionLabel={(opt) => opt.name || ''}
                                    isOptionEqualToValue={(opt, val) => opt._id === val._id}
                                    value={product}
                                    onChange={(e, newValue) => setProduct(newValue)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Choose a product" size="small" />
                                    )}
                                />
                            )}

                            {productTargetMode === 'category' && (
                                <TextField
                                    label="Choose a category"
                                    select
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value)}
                                >
                                    {categoryOptions.map((opt) => (
                                        <MenuItem value={opt._id} key={opt._id}>{opt.label}</MenuItem>
                                    ))}
                                </TextField>
                            )}

                            {productTargetMode === 'multiple' && (
                                <Autocomplete
                                    multiple
                                    options={productOptions}
                                    getOptionLabel={(opt) => opt.name || ''}
                                    isOptionEqualToValue={(opt, val) => opt._id === val._id}
                                    value={products}
                                    onChange={(e, newValue) => setProducts(newValue)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Choose products" size="small" />
                                    )}
                                />
                            )}
                        </div>
                    )}

                    {/* Alert type */}
                    {type === 'alert' && (
                        <div className="flex flex-col gap-2">
                            <p className="text-xs text-gray-500">
                                Leave blank to send to all users, or choose specific people to notify.
                            </p>
                            <Autocomplete
                                multiple
                                options={userOptions}
                                getOptionLabel={(opt) => `${opt.name} (${opt.email})`}
                                isOptionEqualToValue={(opt, val) => opt._id === val._id}
                                value={targetUsers}
                                onChange={(e, newValue) => setTargetUsers(newValue)}
                                renderInput={(params) => (
                                    <TextField {...params} label="Specific users (optional — default: all users)" size="small" />
                                )}
                            />
                        </div>
                    )}
                </div>
                {/* --- Type selector --- */}

                <div>
                    <p className="text-sm text-gray-600 mb-1">Notification Text</p>
                    <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        rows={3}
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        placeholder="What the customer will see in the notification"
                    />
                </div>

                <div className="flex justify-end">
                    <input form="notificationform" type="submit" className="bg-primary-orange uppercase px-6 py-3 text-white font-medium rounded shadow hover:shadow-lg cursor-pointer" value={isEditMode ? "Save Changes" : "Create Notification"} />
                </div>

            </form>
        </>
    );
};

export default NotificationForm;
